import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SERIES_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import { useBookAppointmentCollection } from '@/hooks/use-book-appointment-collection.ts';
import { useSeriesAvailability } from '@/hooks/use-series-availability.ts';
import { useUpdateCollectionWithGuard } from '@/hooks/use-update-collection-with-guard.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { settleAvailabilityInteraction, startAvailabilityInteraction } from '@/lib/availability-telemetry.ts';

import {
    CollectionStep,
    PlacedAppointment,
    SeriesPlacementConfig,
    SeriesPlanningMode,
} from '@/models/appointment/Appointment.ts';
import { AvailabilityAction, MetricSource } from '@/models/metrics.ts';

import { showErrorNotification } from '@/services/notificationService.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { hasOccurrenceConflict } from '@/utils/appointmentUtils.ts';
import { enterSeriesPlacementMode } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

const toTimestamp = (datetime: string): number => dayjs(datetime).valueOf();

const NO_OCCURRENCES_FOUND_MESSAGE = 'Keine Termine gefunden. Bitte passen Sie die Konfiguration an.';

const startSeriesInteraction = (action: AvailabilityAction): void => {
    startAvailabilityInteraction({ key: SERIES_INTERACTION_KEY, mode: 'series', action: action });
};

// An empty answer is a success: the check worked and the answer was no.
const settleSeriesSuccess = (source: MetricSource): void => {
    settleAvailabilityInteraction({ key: SERIES_INTERACTION_KEY, outcome: 'success', source: source });
};

interface UseSeriesFooterResult {
    collectionStep: CollectionStep;
    isBooking: boolean;
    isFetchingSuggestion: boolean;
    isAutoSuggestionMode: boolean;
    canOfferAutoSuggestion: boolean;
    isEditing: boolean;
    hasSuggestion: boolean;
    hasPreviousSuggestion: boolean;
    cannotProceed: boolean;
    cannotBook: boolean;
    showDuplicationPopup: boolean;
    setShowDuplicationPopup: (visible: boolean) => void;
    showRescheduleModal: boolean;
    setShowRescheduleModal: (visible: boolean) => void;
    handleSeriesNext: () => Promise<void>;
    handleAutoSuggestion: () => Promise<void>;
    handleNextSuggestion: () => Promise<void>;
    handlePreviousSuggestion: () => void;
    handleSeriesBack: () => void;
    handleSeriesConfirm: () => void;
    handleCalendarPlacement: () => void;
    handleConfirmDuplication: () => Promise<void>;
    updateCollectionWithGuard: (notifyPatient: boolean) => void;
}

export const useSeriesFooter = (onClose?: () => void): UseSeriesFooterResult => {
    const navigate = useNavigate();
    const { location_key: locationKey } = useConfig();
    const { bookCollection } = useBookAppointmentCollection();
    const updateCollectionWithGuard = useUpdateCollectionWithGuard(onClose);
    const [showDuplicationPopup, setShowDuplicationPopup] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
    const {
        is_last_form_valid: isLastFormValid,
        is_booking_loading: isBooking,
        current_patient: currentPatient,
        collectionStep,
        collection_configuration: collectionConfiguration,
        collection_occurrences: collectionOccurrences,
        collection_suggestion_history: collectionSuggestionHistory,
        editingCollectionId,
        collectionPastAppointments,
        collectionOriginalFutureSlots,
    } = useBookingStore((state) => state);

    const currentForm = useBookingStore(selectCurrentForm);
    const { fetchSeriesAvailability, fetchSeriesAutoSuggestion } = useSeriesAvailability();
    const { data: enrichedPractitionerData, is_loading: isPractitionerLoading } =
        useEnrichedPractitionerDataForSpecificInstance(currentPatient?.instance_id ?? '');

    const isAutoSuggestionMode = collectionConfiguration?.planning_mode === SeriesPlanningMode.AUTO;
    const canOfferAutoSuggestion = collectionConfiguration?.planning_mode !== SeriesPlanningMode.AUTO;
    const hasUnavailableSlots = collectionOccurrences.some((_, index) =>
        hasOccurrenceConflict(collectionOccurrences, index)
    );
    const isEditing = !!editingCollectionId;
    const hasSuggestion = collectionOccurrences.length > 0;
    const hasPreviousSuggestion = collectionSuggestionHistory.length > 0;
    const cannotProceed = !isLastFormValid || isBooking;
    const cannotBook = hasUnavailableSlots || isBooking || isFetchingSuggestion;

    const notifyPractitionerUnresolved = (): void => {
        showErrorNotification(
            isPractitionerLoading
                ? 'Daten werden geladen. Bitte versuchen Sie es erneut.'
                : 'Behandler konnte nicht ermittelt werden. Bitte Kalender erneut auswählen.'
        );
    };

    const handleSeriesNext = async (): Promise<void> => {
        startSeriesInteraction(isAutoSuggestionMode ? 'auto_suggest' : 'open_slots');
        const fetchOccurrences = isAutoSuggestionMode ? fetchSeriesAutoSuggestion : fetchSeriesAvailability;

        useBookingStore.setState({ is_booking_loading: true });
        const occurrences = await fetchOccurrences();
        if (!occurrences) {
            useBookingStore.setState({ is_booking_loading: false });
            return;
        }
        if (occurrences.length === 0) {
            useBookingStore.setState({ is_booking_loading: false });
            showErrorNotification(NO_OCCURRENCES_FOUND_MESSAGE);
        } else {
            useBookingStore.setState({
                collectionStep: CollectionStep.LIST,
                collection_occurrences: occurrences,
                is_booking_loading: false,
            });
        }
        settleSeriesSuccess('network');
    };

    const handleAutoSuggestion = async (): Promise<void> => {
        startSeriesInteraction('auto_suggest');

        if (!collectionConfiguration) {
            settleAvailabilityInteraction({
                key: SERIES_INTERACTION_KEY,
                outcome: 'failure',
                reason: 'availability_config_incomplete',
            });
            return;
        }

        const autoConfig = { ...collectionConfiguration, planning_mode: SeriesPlanningMode.AUTO };

        useBookingStore.setState({ is_booking_loading: true, collection_configuration: autoConfig });
        const occurrences = await fetchSeriesAutoSuggestion();
        if (!occurrences) {
            useBookingStore.setState({ is_booking_loading: false, collection_configuration: collectionConfiguration });
            return;
        }
        if (occurrences.length === 0) {
            useBookingStore.setState({ is_booking_loading: false, collection_configuration: collectionConfiguration });
            showErrorNotification(NO_OCCURRENCES_FOUND_MESSAGE);
        } else {
            useBookingStore.setState({
                collection_occurrences: occurrences,
                is_booking_loading: false,
            });
        }
        settleSeriesSuccess('network');
    };

    const handleSeriesBack = (): void => {
        useBookingStore.setState({
            collectionStep: CollectionStep.CONFIG,
            collection_occurrences: [],
            collection_suggestion_history: [],
        });
    };

    const handleNextSuggestion = async (): Promise<void> => {
        const earliestSlot = collectionOccurrences
            .map((occurrence) => occurrence.start_datetime)
            .sort((first, second) => first.localeCompare(second))[0];
        if (!earliestSlot) return;
        const nextAnchor = dayjs.utc(earliestSlot).add(1, 'day').format('YYYY-MM-DD');

        startSeriesInteraction('next_suggestion');
        setIsFetchingSuggestion(true);
        const occurrences = await fetchSeriesAutoSuggestion(nextAnchor);
        setIsFetchingSuggestion(false);
        // undefined = fetch failed (runFetch already notified); [] = no further suggestions.
        if (!occurrences) return;
        if (occurrences.length === 0) {
            showErrorNotification('Keine weiteren Vorschläge verfügbar.');
        } else {
            useBookingStore.setState({
                collection_occurrences: occurrences,
                collection_suggestion_history: [...collectionSuggestionHistory, collectionOccurrences],
            });
        }
        settleSeriesSuccess('network');
    };

    // Measured even though this only pops a stack today, so a future refetch here lands in the same metric.
    const handlePreviousSuggestion = (): void => {
        const previous = collectionSuggestionHistory.at(-1);
        if (!previous) return;
        startSeriesInteraction('previous_suggestion');
        useBookingStore.setState({
            collection_occurrences: previous,
            collection_suggestion_history: collectionSuggestionHistory.slice(0, -1),
        });
        settleSeriesSuccess('cache');
    };

    const hasScheduleChanged = (): boolean => {
        if (collectionOccurrences.length !== collectionOriginalFutureSlots.length) return true;
        const sortedNew = collectionOccurrences
            .map((occurrence) => toTimestamp(occurrence.start_datetime))
            .sort((first, second) => first - second);
        const sortedOld = [...collectionOriginalFutureSlots].map(toTimestamp).sort((first, second) => first - second);
        return sortedNew.some((timestamp, index) => timestamp !== sortedOld[index]);
    };

    const handleSeriesConfirm = (): void => {
        if (editingCollectionId && hasScheduleChanged()) {
            setShowRescheduleModal(true);
            return;
        }
        if (editingCollectionId) {
            updateCollectionWithGuard(false);
            return;
        }
        void (async (): Promise<void> => {
            const result = await bookCollection({ onClose: onClose, isOverlap: false });
            if (result?.needsDuplication) {
                setShowDuplicationPopup(true);
            }
        })();
    };

    const handleCalendarPlacement = (): void => {
        const config = useBookingStore.getState().collection_configuration;
        if (!config || !currentForm) {
            showErrorNotification('Fehlende Konfiguration für die Kalenderplatzierung');
            return;
        }

        const appointmentType = enrichedPractitionerData?.appointment_types.find(
            (type) => type.id === currentForm.apt_type_id_or_schedule_type_pvs_id
        );
        if (!appointmentType) return;

        const practitioner = enrichedPractitionerData?.practitioners.find((candidate) =>
            candidate.calendars.some((calendar) => calendar.pvs.id === currentForm.calendar_pvs_id)
        );
        if (!practitioner) {
            notifyPractitionerUnresolved();
            return;
        }
        const calendar = practitioner.calendars.find((entry) => entry.pvs.id === currentForm.calendar_pvs_id);
        const calendarName =
            practitioner.calendars.length === 1 ? (practitioner.name ?? '') : (calendar?.pvs.name ?? '');

        const placementConfig: SeriesPlacementConfig = {
            patientName:
                `${currentPatient?.personal.last_name ?? ''} ${currentPatient?.personal.first_name ?? ''}`.trim(),
            appointmentTypeName: appointmentType?.name ?? '',
            calendarName: calendarName,
            durationMinutes: appointmentType?.duration?.value ?? 30,
            totalAppointments: config.number_of_appointments ?? 6,
            practitionerCalendarId: currentForm.calendar_pvs_id ?? '',
            previousCalendarSelection: useFilterStore.getState().selected_calendars[locationKey] ?? [],
        };

        const pastPlacements: PlacedAppointment[] = editingCollectionId
            ? collectionPastAppointments.map((appointment, index) => ({
                  id: appointment.id,
                  position: index + 1,
                  startDatetime: appointment.start,
                  endDatetime: appointment.end,
                  isPast: true,
              }))
            : [];

        enterSeriesPlacementMode({
            placementConfig: placementConfig,
            locationKey: locationKey,
            navigate: navigate,
            initialPlacements: pastPlacements,
        });
    };

    const handleConfirmDuplication = async (): Promise<void> => {
        useBookingStore.setState({ patient_duplication_allowed: true });
        setShowDuplicationPopup(false);
        const result = await bookCollection({ onClose: onClose, isOverlap: false });
        if (result?.needsDuplication) {
            useBookingStore.setState({ patient_duplication_allowed: false });
            showErrorNotification('Fehler bei der Patientenduplizierung. Bitte versuchen Sie es erneut.');
        }
    };

    return {
        collectionStep,
        isBooking,
        isFetchingSuggestion,
        isAutoSuggestionMode,
        canOfferAutoSuggestion,
        isEditing,
        hasSuggestion,
        hasPreviousSuggestion,
        cannotProceed,
        cannotBook,
        showDuplicationPopup,
        setShowDuplicationPopup,
        showRescheduleModal,
        setShowRescheduleModal,
        handleSeriesNext,
        handleAutoSuggestion,
        handleNextSuggestion,
        handlePreviousSuggestion,
        handleSeriesBack,
        handleSeriesConfirm,
        handleCalendarPlacement,
        handleConfirmDuplication,
        updateCollectionWithGuard,
    };
};
